import { AddPrimitivesCommand, BasicControl, DYMODE_MODE, ExtrudeSolidT, Line, LineT, Listeners, MOUSE, Point, Polygon, TnEngineContext, TnEngineExtraContext, ViewEditor, dymode } from 'pytha';
import { Vector3 } from 'three';
import { ARCHI_CTRL_TYPE } from '../enums/archibase-control-type';
import { getSpecialPrimitiveLayerUuid } from '@/commons/utils/layer-utils';
import { LayerType } from '@/commons/enums/layer-type';


const STEP = {
    1: { 'tip': '指定屋面板的第一个边点', 'mode': DYMODE_MODE.POINT },
    2: { 'tip': '指定屋面板的第二个边点', 'mode': DYMODE_MODE.POINT },
    3: { 'tip': '指定檐口板的方向', 'mode': DYMODE_MODE.POINT },
}

export class AssignEavesBoardControl extends BasicControl {

    id = ARCHI_CTRL_TYPE.ASSIGN_EAVES_BOARD;

    point_down: Point = new Point();
    point_move: Point = new Point();
    dymodePoint: Vector3 = new Vector3();
    capturePoint: Vector3;

    step: number = 1;

    helpExt: ExtrudeSolidT;
    helpLine: LineT;
    helpDir: Vector3 = new Vector3(1, 0, 0)

    point_1: Point;
    point_2: Point;

    context: TnEngineContext;
    extraContext: TnEngineExtraContext;

    isInit: boolean = false
    height: number = 0
    thickness: number = 0

    boardDir: Vector3 = new Vector3()

    projectUuid: string = ""
    modelUuid: string = ""

    constructor(editor: ViewEditor, listeners: Listeners) {
        super()
        this.needInit = true;
        this.editor = editor;
        this.listeners = listeners;
        this.context = TnEngineContext.getInstance();
        this.extraContext = TnEngineExtraContext.getInstance();
        this.listeners.signals.needCapture.dispatch();

        editor.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
        editor.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
        listeners.signals.onPointCapture.add(this.onPointCapture);
        listeners.signals.setInitControlData.add(this.initControl)
        listeners.signals.onDymodeChanged.add(this.onDymodeChanged);
        this.editor.clampControl.enabled = false;
        // 钝化选择
        this.editor.selectControl.setEnabled(false);
        this.listeners.signals.emptySelectedEntities.dispatch();
        // 激活动态输入
        this.extraContext.dymodeContext.enabled = true;
        // 派发动态输入信息
        this.listeners.signals.sendToDymodeTip.dispatch(STEP[this.step].tip + ':');
        this.extraContext.dymodeContext.setMode(STEP[this.step].mode);
        this.extraContext.stmodeContext.setStmode(STEP[this.step].tip);
        console.log("AssignEavesBoardControl add");
    }

    dispose(): void {
        this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        this.editor.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
        this.listeners.signals.onPointCapture.remove(this.onPointCapture);
        this.listeners.signals.setInitControlData.remove(this.initControl)
        this.listeners.signals.onDymodeChanged.remove(this.onDymodeChanged);
        this.listeners.signals.noNeedCapture.dispatch();
        this.editor.selectControl.setEnabled(true);
        this.extraContext.dymodeContext.enabled = false;
        this.editor.clampControl.enabled = true;
        this.editor.orthogonalControl.enabled = false;
        this.editor.polarControl.enabled = false;

        let obj = this?.helpExt?.viewObj;
        if (obj && !!obj.parent) {
            obj.parent.remove(obj);
        }

        let lineObj = this?.helpLine?.viewObj;
        if (lineObj && !!lineObj.parent) {
            lineObj.parent.remove(lineObj);
        }
        console.log("AssignEavesBoardControl removed");
    }

    initControl = (data: any) => {
        this.height = data.height
        this.thickness = data.thickness
        this.projectUuid = data.projectUuid
        this.modelUuid = data.modelUuid
        this.isInit = true
    }

    onPointCapture = (point) => {
        this.capturePoint = point;
    }

    onPointerDown = (event) => {
        if (event.button === MOUSE.RIGHT || !this.isInit) {
            this.listeners.signals.onOpeCommandControlEnd.dispatch();
            return;
        }
        if (event.button !== MOUSE.LEFT) return;
        this.point_down.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        if (this.capturePoint) {
            this.point_down.copy(this.capturePoint);
        }

        if (this.step == 1) {
            this.step_1()
        } else if (this.step == 2) {
            this.step_2()
        } else if (this.step == 3) {
            this.step_3()
        }
    }

    mirrorLine: LineT;
    onPointerMove = () => {
        const x = this.extraContext.mousePosContext.x;
        const y = this.extraContext.mousePosContext.y;
        const z = this.extraContext.mousePosContext.z;
        this.point_move.set(x, y, z);
        if (this.capturePoint) {
            this.point_move.copy(this.capturePoint);
        }
        switch (this.step) {
            case 1:
                // 无任何操作
                break;
            case 2:
                // 移动helpLine
                this.helpLine.set(this.point_1, this.point_move)
                this.helpLine.updateViewGeometry()
                break;
            case 3:
                (this.helpDir.copy(this.point_move)).sub(this.point_1).normalize()
                // 判断是否需要mirror
                if (this.helpDir.dot(this.boardDir) < 0) {
                    this.helpExt.mirror(this.mirrorLine)
                    this.helpExt.updateViewGeometry()
                    this.boardDir.negate()
                }
                break;
            default:
                break;
        }
    }

    onDymodeChanged = (value: dymode) => {
        if (value.mode === DYMODE_MODE.POINT) {
            this.dymodePoint.set(value.x, value.y, value.z);
            this.onKeyMouseConfirm();
        }
    }

    onKeyMouseConfirm = () => {
        this.point_down.copy(this.dymodePoint);
        if (this.step == 1) {
            this.step_1()
        } else if (this.step == 2) {
            this.step_2()
        } else if (this.step == 3) {
            this.step_3()
        }
    }

    step_1 = () => {
        this.point_1 = this.point_down.clone()
        this.helpLine = new LineT(this.point_1, this.point_1)
        let lineView = this.helpLine.render()
        this.extraContext.sceneContext.scene.add(lineView)
        this.step++
        this.listeners.signals.sendToDymodeTip.dispatch(STEP[this.step].tip + ':');
        this.extraContext.dymodeContext.setMode(STEP[this.step].mode);
        this.extraContext.stmodeContext.setStmode(STEP[this.step].tip);
    }

    step_2 = () => {
        this.point_2 = this.point_down.clone()
        // 初始化镜像线
        this.mirrorLine = new LineT(this.point_1, this.point_2)
        // 竖向方向
        let yDir: Vector3 = (new Vector3().copy(this.point_2).sub(this.point_1)).normalize()
        // 横向方向
        let xDir: Vector3 = (yDir.clone().cross(new Vector3(0, 0, 1))).normalize()
        this.boardDir = xDir.clone()
        // 获取四个点
        let bottomPoint_1: Point = this.point_1.clone()
        let bottomPoint_2: Point = this.point_2.clone()
        let bottomPoint_3: Point = bottomPoint_2.clone().add(xDir.clone().multiplyScalar(this.thickness))
        let bottomPoint_4: Point = bottomPoint_1.clone().add(xDir.clone().multiplyScalar(this.thickness))
        // 获取底面
        let bottomFace: Polygon = new Polygon([
            new Line(bottomPoint_1, bottomPoint_2),
            new Line(bottomPoint_2, bottomPoint_3),
            new Line(bottomPoint_3, bottomPoint_4),
            new Line(bottomPoint_4, bottomPoint_1)
        ])
        // 初始化檐口板
        this.helpExt = new ExtrudeSolidT(bottomFace, new Vector3(0, 0, -1), this.height)
        this.extraContext.sceneContext.scene.add(this.helpExt.render())
        this.step++
        this.listeners.signals.sendToDymodeTip.dispatch(STEP[this.step].tip + ':');
        this.extraContext.dymodeContext.setMode(STEP[this.step].mode);
        this.extraContext.stmodeContext.setStmode(STEP[this.step].tip);

        let lineObj = this?.helpLine?.viewObj;
        if (lineObj && !!lineObj.parent) {
            lineObj.parent.remove(lineObj);
        }
    }

    step_3 = () => {
        (this.helpDir.copy(this.point_down)).sub(this.point_1).normalize()
        // 判断是否需要mirror
        if (this.helpDir.dot(this.boardDir) < 0) {
            this.helpExt.mirror(this.mirrorLine)
            this.helpExt.updateViewGeometry()
            this.boardDir.negate()
        }

        getSpecialPrimitiveLayerUuid(this.projectUuid,this.modelUuid, LayerType.EAVES_BOARD).then((layerUuid: string) => {
            this.helpExt.layerUuid = layerUuid
            let command = new AddPrimitivesCommand([this.helpExt.clone()])
            this.editor.history.execute(command);
            this.listeners.signals.onOpeCommandControlEnd.dispatch();
        })


    }
}